// Maeck Office — frame pacing of the 3D scene (SPEC §10). Pure helpers, no DOM, no three.js.
//
// The scene only redraws when something changes, at a rate that depends on what moves: user
// motion (camera transitions, drags) stays smooth, working agents are drawn at a film-like rate,
// idle breathing at a slow ambient rate, and a still office is not redrawn at all. The owner opens
// several offices at once on a laptop: every number that costs GPU time lives here, in one place.

/**
 * Scene cost settings (edit here to trade smoothness for heat).
 * - motionFps: camera transitions, drags, hover — short and user-driven.
 * - activeFps: agents working, particles, connector pulses (small typing/walking animations).
 * - ambientFps: idle breathing only (nothing else moves).
 * - maxPixelRatio: cap of the canvas resolution (Retina screens report 2 or 3).
 * - antialiasBelowRatio: MSAA only on screens whose devicePixelRatio is below this (on Retina
 *   screens the extra pixels already smooth the edges).
 * - shadowMapSize: resolution of the sun's shadow map.
 */
export const SCENE_PACING = Object.freeze({
  motionFps: 60,
  activeFps: 24,
  ambientFps: 8,
  maxPixelRatio: 1.5,
  antialiasBelowRatio: 2,
  shadowMapSize: 1024,
});

/** Frames closer than this (ms) to their due time are drawn now (vsync jitter). */
const DUE_SLACK_MS = 2;

/**
 * Frame rate wanted for what currently moves; 0 = nothing moves, do not redraw.
 * @param {{ motion?: boolean, active?: boolean, ambient?: boolean }} state
 * @param {typeof SCENE_PACING} [pacing]
 * @returns {number}
 */
export function targetFps(state, pacing = SCENE_PACING) {
  if (state.motion) return pacing.motionFps;
  if (state.active) return pacing.activeFps;
  if (state.ambient) return pacing.ambientFps;
  return 0;
}

/**
 * Timestamp throttle for requestAnimationFrame: decides whether the frame at `now` is drawn at
 * `fps`, and returns the reference time for the next decision. The reference advances by whole
 * intervals (not to `now`), so a 24 fps cap on a 60 Hz screen averages 24 fps instead of falling
 * to 20; after a long pause it restarts from `now` (no burst of catch-up frames). Simulation uses
 * the real elapsed time, so animations keep their real-time speed whatever the rate.
 * @param {number} now rAF timestamp (ms)
 * @param {number} lastRender reference time of the last drawn frame (ms); 0 = none yet
 * @param {number} fps cap; 0 or ≥ the screen rate draws every frame
 * @returns {{ render: boolean, next: number, waitMs: number }} next = new reference when drawn;
 *   waitMs = time until the next frame is due (0 when drawn)
 */
export function throttleFrame(now, lastRender, fps) {
  if (!lastRender || !(fps > 0)) return { render: true, next: now, waitMs: 0 };
  const interval = 1000 / fps;
  const elapsed = now - lastRender;
  if (elapsed < 0) return { render: true, next: now, waitMs: 0 };
  if (elapsed + DUE_SLACK_MS < interval) return { render: false, next: lastRender, waitMs: interval - elapsed };
  const steps = Math.max(1, Math.floor((elapsed + DUE_SLACK_MS) / interval));
  const next = elapsed > interval * 2 ? now : lastRender + steps * interval;
  return { render: true, next, waitMs: 0 };
}

/**
 * Canvas pixel ratio: the device ratio, capped.
 * @param {number} [deviceRatio]
 * @param {typeof SCENE_PACING} [pacing]
 */
export function scenePixelRatio(deviceRatio, pacing = SCENE_PACING) {
  const r = Number.isFinite(deviceRatio) && deviceRatio > 0 ? deviceRatio : 1;
  return Math.min(r, pacing.maxPixelRatio);
}

/**
 * Whether the renderer should use MSAA on this screen.
 * @param {number} [deviceRatio]
 * @param {typeof SCENE_PACING} [pacing]
 */
export function wantsAntialias(deviceRatio, pacing = SCENE_PACING) {
  const r = Number.isFinite(deviceRatio) && deviceRatio > 0 ? deviceRatio : 1;
  return r < pacing.antialiasBelowRatio;
}
